"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, Minus, Plus, X } from "lucide-react";
import { downloadUrl, filenameFromUrl } from "@/lib/download-file";
import type { LightboxImage } from "@/lib/image-lightbox-helper";
import { useShortcut } from "@/lib/shortcuts/useShortcut";
import { useBodyScrollLock } from "@/components/tailwind/ui/anchored-popover";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

const TOOL_BUTTON =
  "flex h-9 w-9 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/15 hover:text-white disabled:pointer-events-none disabled:opacity-30";

const NAV_BUTTON =
  "pointer-events-auto absolute top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white/70 transition-colors hover:bg-black/70 hover:text-white";

export interface ImageLightboxProps {
  images: LightboxImage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

export function ImageLightbox({ images, index, onIndexChange, onClose }: ImageLightboxProps) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const pannedRef = useRef(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const current = images[index];
  const hasMany = images.length > 1;
  const currentSrc = current?.src;

  const applyZoom = useCallback((next: number) => {
    const value = clampZoom(next);
    setZoom(value);
    if (value === MIN_ZOOM) setOffset({ x: 0, y: 0 });
  }, []);

  const step = useCallback(
    (delta: number) => {
      if (images.length < 2) return;
      onIndexChange((index + delta + images.length) % images.length);
    },
    [images.length, index, onIndexChange],
  );

  // A modal owns the keyboard. Taking focus blurs the editor, so bare keys
  // reach the window dispatcher instead of being eaten by ProseMirror.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // Every image starts unzoomed and centered.
  useEffect(() => {
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });
  }, [currentSrc]);

  useBodyScrollLock(true);

  // Non-passive so the page behind the overlay can't scroll while zooming.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      applyZoom(zoomRef.current - event.deltaY * 0.002 * zoomRef.current);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyZoom]);

  useShortcut({
    id: "image.lightbox.close",
    keys: "Escape",
    scope: "modal",
    label: "Close image preview",
    group: "Image",
    allowInInput: true,
    run: () => {
      onClose();
    },
  });

  useShortcut({
    id: "image.lightbox.prev",
    keys: "ArrowLeft",
    scope: "modal",
    label: "Previous image",
    group: "Image",
    allowInInput: true,
    when: () => hasMany,
    run: () => {
      step(-1);
    },
  });

  useShortcut({
    id: "image.lightbox.next",
    keys: "ArrowRight",
    scope: "modal",
    label: "Next image",
    group: "Image",
    allowInInput: true,
    when: () => hasMany,
    run: () => {
      step(1);
    },
  });

  if (!current) return null;

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    pannedRef.current = false;
    if (zoom <= MIN_ZOOM) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) pannedRef.current = true;
    setOffset({ x: drag.ox + dx, y: drag.oy + dy });
  };

  const onPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer already released (capture lost on leaving the window).
    }
  };

  return createPortal(
    <div
      // Marks the overlay so the block-selection plugin's outside-mousedown
      // handler leaves the image's NodeSelection — and its resizer — alone.
      data-image-overlay="true"
      // A scrim, not a blackout. Dark enough that the page behind reads as one
      // flat surface rather than patches of sidebar and header showing through,
      // but not so dark the viewer feels like a separate screen. No blur — it
      // muddies the background without making the image read any better.
      className="fixed inset-0 z-[10000] bg-black/80"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={current.alt || "Image preview"}
        tabIndex={-1}
        className="relative flex h-full w-full flex-col outline-none"
      >
        {/* Dismiss layer under everything: any click that is not the image or a
            control lands here. The rows above are pointer-transparent except
            for their own controls, so the whole surround stays clickable. */}
        <button
          type="button"
          aria-label="Close image preview"
          onClick={onClose}
          className="absolute inset-0 cursor-default"
        />

        {/* Its own row, not an overlay — the toolbar and the image share one
            stacking context, so an absolutely positioned toolbar was painted
            over by any image tall enough to reach it. */}
        <header className="pointer-events-none relative z-20 flex h-14 shrink-0 items-center gap-1 px-3">
          {hasMany && (
            <span className="pointer-events-auto text-[13px] font-medium tabular-nums text-white/60">
              {index + 1} / {images.length}
            </span>
          )}
          <div className="pointer-events-auto ml-auto flex items-center gap-0.5">
            <button
              type="button"
              aria-label="Zoom out"
              title="Zoom out"
              disabled={zoom <= MIN_ZOOM}
              onClick={() => applyZoom(zoom - ZOOM_STEP)}
              className={TOOL_BUTTON}
            >
              <Minus className="h-[18px] w-[18px]" />
            </button>
            <span className="min-w-[3.25rem] text-center text-[13px] font-medium tabular-nums text-white/70">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              aria-label="Zoom in"
              title="Zoom in"
              disabled={zoom >= MAX_ZOOM}
              onClick={() => applyZoom(zoom + ZOOM_STEP)}
              className={TOOL_BUTTON}
            >
              <Plus className="h-[18px] w-[18px]" />
            </button>
            <span className="mx-1.5 h-5 w-px bg-white/15" />
            <button
              type="button"
              aria-label="Download image"
              title="Download"
              onClick={() => void downloadUrl(current.src, filenameFromUrl(current.src))}
              className={TOOL_BUTTON}
            >
              <Download className="h-[18px] w-[18px]" />
            </button>
            <button type="button" aria-label="Close" title="Close" onClick={onClose} className={TOOL_BUTTON}>
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
        </header>

        <div
          ref={stageRef}
          className="pointer-events-none relative z-10 flex min-h-0 flex-1 items-center justify-center overflow-hidden"
        >
          {/* A button, not a bare <img>, so the zoom toggle is reachable by
              keyboard as well as by pointer. */}
          <button
            type="button"
            aria-label={zoom > MIN_ZOOM ? "Reset zoom" : "Zoom in"}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            // A click that merely ended a pan must not also toggle the zoom.
            onClick={() => {
              if (pannedRef.current) return;
              applyZoom(zoom > MIN_ZOOM ? MIN_ZOOM : 2);
            }}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})` }}
            // No size of its own — it shrink-wraps the image, so the clickable
            // area is exactly the picture and never the scrim beside it.
            className={`pointer-events-auto relative flex ${
              zoom > MIN_ZOOM ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"
            }`}
          >
            {/* Viewport-relative caps, not max-h-full: a percentage max-height
                inside the wrapper stopped the browser preserving the aspect
                ratio, leaving dead click zones either side of the picture. The
                subtrahend is the header (3.5rem) plus the top and bottom gaps.
                No drop shadow either — over a translucent scrim it reads as a
                second, differently-dark band around the image, not as depth. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={current.src}
              alt={current.alt || ""}
              draggable={false}
              className="block max-h-[calc(100dvh_-_6rem)] max-w-[calc(100vw_-_2rem)] select-none object-contain [-webkit-user-drag:none] sm:max-h-[calc(100dvh_-_7.5rem)] sm:max-w-[calc(100vw_-_6rem)]"
            />
          </button>

          {hasMany && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={() => step(-1)}
                className={`${NAV_BUTTON} left-3`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={() => step(1)}
                className={`${NAV_BUTTON} right-3`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ImageLightbox;
